// src/features/learner/components/Navigation/InternalPagination.js
import React from 'react';

export const InternalPagination = ({
    internalContent,
    currentIndex,
    onGoToIndex,
    visitedPages = new Set(),
    visitedUnits = new Set(),
    completedUnits = {},
    currentContent = null
}) => {
    if (!internalContent || internalContent.length <= 1) {
        return null;
    }

    const totalPages = internalContent.length;

    const getVisiblePages = () => {
        const WINDOW_SIZE = 5;
        const halfWindow = Math.floor(WINDOW_SIZE / 2);
        let start = Math.max(0, currentIndex - halfWindow);
        let end = Math.min(totalPages - 1, currentIndex + halfWindow);
        if (end - start + 1 < WINDOW_SIZE) {
            if (start === 0) end = Math.min(totalPages - 1, WINDOW_SIZE - 1);
            else if (end === totalPages - 1) start = Math.max(0, totalPages - WINDOW_SIZE);
        }
        return { start, end };
    };

    const { start, end } = getVisiblePages();
    const showStartEllipsis = start > 0;
    const showEndEllipsis = end < totalPages - 1;

    // ---------------------------------------------------------------------------
    // Question types that require explicit user input before turning teal.
    // isQuestion is now evaluated per-item (inside the map) using item.type,
    // NOT from currentContent.type. A story package can contain MCQ items —
    // checking the wrapper type was causing question dots to turn teal on visit
    // before the learner had answered anything.
    // ---------------------------------------------------------------------------
    const questionTypes = [
        'flipcard', 'flip-card', 'lrc', 'LRC', 'listen-record-compare',
        'Type-in', 'MCQ', 'Multiple-response', 'Matching', 'Order'
    ];

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px',
                      justifyContent: 'center', flexWrap: 'nowrap' }}>

            {showStartEllipsis && (
                <span style={{ color: '#999', fontSize: '14px' }}>…</span>
            )}

            {internalContent.slice(start, end + 1).map((item, idx) => {
                const index = start + idx;
                const isActive = index === currentIndex;
                const itemId = item.id;

                // ------------------------------------------------------------------
                // FIX 1: isQuestion is now per-item, using item.type.
                // Previously this was computed once outside the map from
                // currentContent.type (the package wrapper), which meant all dots
                // in a story package were treated as non-questions, turning teal
                // immediately on visit even for MCQ items inside the package.
                // ------------------------------------------------------------------
                const isQuestion = questionTypes.includes(item.type);

                // completedUnits values written by renderers:
                //   'correct'      — close-ended, right answer
                //   'wrong'        — close-ended, wrong answer
                //   'learnt'       — flipcard "I Know This"
                //   'dunno'        — flipcard "Still Learning"
                //   'completed'    — LRC "I Know This"
                //   'still_learning' — LRC "Still Learning"
                //   true           — open-ended page auto-complete
                const completionStatus = completedUnits[itemId] || completedUnits[`page_${index}`];

                const isWrong    = completionStatus === 'wrong'
                                   || completionStatus === 'dunno'
                                   || completionStatus === 'still_learning'
                                   || completionStatus === 'skipped';

                const isMastered = completionStatus === 'correct'
                                   || completionStatus === 'learnt'
                                   || completionStatus === 'completed'
                                   || completionStatus === true;

                // ------------------------------------------------------------------
                // FIX 2: visitedPages ID format.
                // markInternalPageVisited writes: "${contentId}_internal_${pageIndex}"
                // The old checks (itemId and `page_${index}`) never matched that key,
                // so dots for visited open-ended pages stayed white.
                // The canonical key is now checked first.
                // ------------------------------------------------------------------
                const internalKey = `${currentContent?.id}_internal_${index}`;
                const isVisited = visitedPages.has(internalKey)
                                  || visitedPages.has(itemId)
                                  || visitedUnits.has(itemId)
                                  || visitedPages.has(`page_${index}`)
                                  || isWrong || isMastered;

                // Colour logic:
                //   yellow  (#e8a327) — wrong / still learning
                //   teal    (#19a4a0) — correct / mastered / visited open-ended
                //   white   (#f8f9fa) — not yet visited or unanswered question
                let backgroundColor = '#f8f9fa';
                let statusLabel = '';

                if (isWrong) {
                    backgroundColor = '#e8a327';
                    statusLabel = ' (try again)';
                } else if (isMastered) {
                    backgroundColor = '#19a4a0';
                    statusLabel = ' (done)';
                } else if (isVisited && !isQuestion) {
                    // Open-ended page that has been seen — mark teal
                    backgroundColor = '#19a4a0';
                    statusLabel = ' (visited)';
                }
                // If isQuestion and not yet answered — stays white

                const textIsLight = isMastered
                                    || (isVisited && !isQuestion)
                                    || isWrong;

                return (
                    <button
                        key={index}
                        onClick={() => onGoToIndex(index)}
                        title={`Page ${index + 1}${statusLabel}`}
                        style={{
                            backgroundColor,
                            color: textIsLight ? '#fff' : '#333',
                            border: isActive ? '3px solid #ff4747' : '2px solid #dee2e6',
                            borderRadius: '50%',
                            width: '38px',
                            height: '38px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            fontWeight: isActive ? '700' : '500',
                            flexShrink: 0
                        }}
                    >
                        {index + 1}
                    </button>
                );
            })}

            {showEndEllipsis && (
                <span style={{ color: '#999', fontSize: '14px' }}>…</span>
            )}
        </div>
    );
};